import React, { useMemo, useState } from 'react';
import { Row, Col, Input, Select, DatePicker, Typography, Divider, Spin, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import type { QaqcRfiForms } from '../../../types';
import StandardTable from '../../../components/common/StandardTable';
import ActionButtons from '../../../components/common/ActionButtons';
import { useAuth } from '../../../components/common/security/AuthContext';
import { useTablePagination } from '../../../components/common/useTablePagination';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';
import { UseGetQaqcRfiForms } from '../../../hooks/genericHooks';
import { Tooltip } from 'antd';
import { CgAttachment } from 'react-icons/cg';
import Button from '../../../components/common/Button';

const RCgAttachment: React.FC<any> = (props) =>
  React.createElement(CgAttachment as unknown as React.ComponentType<any>, props) as React.ReactElement;

const { Title } = Typography;
const { RangePicker } = DatePicker;

const QaqcRfiFormList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Filters
  const [subjectText, setSubjectText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [recipientsSearch, setRecipientsSearch] = useState('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);

  // Pagination
  const { getPaginationProps } = useTablePagination(15);

  // Data via generic hook
  const { data, isLoading } = UseGetQaqcRfiForms();
  const rfiForms: QaqcRfiForms[] = data?.data || [];

  const filteredData = useMemo(() => {
    const data = rfiForms || [];
    const subj = subjectText.trim().toLowerCase();
    const recSearch = recipientsSearch.trim().toLowerCase();
    const [start, end] = dateRange || [null, null];
    return data.filter(item => {
      const subjectOk = !subj || (item.rfi_subject || '').toLowerCase().includes(subj);
      const statusOk = !statusFilter.length || (item.rfi_status ? statusFilter.includes(item.rfi_status) : false);
      const recipientsStr = Array.isArray(item.recipients) ? item.recipients.join('; ') : '';
      const recipientsOk = !recSearch || recipientsStr.toLowerCase().includes(recSearch);
      let dateOk = true;
      if (start || end) {
        const d = item.request_date ? dayjs(item.request_date) : null;
        if (!d) dateOk = false; else {
          if (start && d.isBefore(start, 'day')) dateOk = false;
          if (end && d.isAfter(end, 'day')) dateOk = false;
        }
      }
      return subjectOk && statusOk && recipientsOk && dateOk;
    });
  }, [rfiForms, subjectText, statusFilter, dateRange, recipientsSearch]);

  const columns: ColumnsType<QaqcRfiForms> = useMemo(() => {
    const filesColWidth = 36;
    const base: any[] = [
      { title: 'Form #', dataIndex: 'form_id', key: 'form_id', width: 90 },
      {
        title: 'Att',
        key: 'has_files',
        width: filesColWidth,
        align: 'center' as const,
        onHeaderCell: () => ({ style: { width: filesColWidth, minWidth: filesColWidth, maxWidth: filesColWidth } }),
        onCell: () => ({ style: { width: filesColWidth, minWidth: filesColWidth, maxWidth: filesColWidth } }),
        render: (_: any, row: QaqcRfiForms) => {
          const has = !!(row as any)?.upload_path && String((row as any).upload_path).trim() !== '';
          return has ? (
            <Tooltip title="Has attachments">
              <RCgAttachment size={16} color="#1677ff" />
            </Tooltip>
          ) : null;
        },
      },
      { title: 'Doc Ref', dataIndex: 'work_permit_ref_num', key: 'work_permit_ref_num', width: 120 },
      { title: 'Description', dataIndex: 'work_permit_description', key: 'work_permit_description', ellipsis: true },
      { title: 'Issued By', dataIndex: 'issued_by', key: 'issued_by', width: 120 },
      { title: 'Due Date', dataIndex: 'request_date', key: 'request_date', width: 100, render: (v: string) => v ? dayjs(v).format('YYYY-MM-DD') : '' },
      { title: 'Subject', dataIndex: 'rfi_subject', key: 'rfi_subject', ellipsis: true },
      { title: 'Recipients', dataIndex: 'recipients', key: 'recipients', width: 240, ellipsis: true, render: (arr?: string[]) => Array.isArray(arr) && arr.length ? arr.join('; ') : '' },
      { title: 'Appointees', dataIndex: 'appointees', key: 'appointees', width: 240, ellipsis: true, render: (arr?: string[]) => Array.isArray(arr) && arr.length ? arr.join('; ') : '' },
      { title: 'Invitees', dataIndex: 'invitees', key: 'invitees', width: 240, ellipsis: true, render: (arr?: string[]) => Array.isArray(arr) && arr.length ? arr.join('; ') : '' },
      { title: 'Status', dataIndex: 'rfi_status', key: 'rfi_status', width: 80, render: (s?: string) => {
        const color = s === 'active' ? 'green' : s === 'archived' ? 'default' : s === 'cancelled' ? 'red' : 'gold';
        return <Tag color={color}>{s || 'draft'}</Tag>;
      } },
      { title: 'Created@', dataIndex: 'created_at', key: 'created_at', width: 100, render: (v?: string) => v ? dayjs(v).format('YYYY-MM-DD') : '' },
    ];
    const actionCol: any = {
      title: 'Action',
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_: any, row: QaqcRfiForms) => {
        const currentPid = localStorage.getItem('personnel_id') || '';
        const recipientIds: string[] = Array.isArray((row as any).recipient_ids) ? (row as any).recipient_ids : [];
        const notCancelled = row.rfi_status !== 'cancelled';
        const canEdit = notCancelled && (currentPid ? recipientIds.includes(currentPid) : false);
        return (
          <ActionButtons
            onView={() => navigate(`/qaqc/rfi/view/${row.id}`)}
            onEdit={canEdit ? () => navigate(`/qaqc/rfi/edit/${row.id}`) : undefined}
            showView={true}
            showEdit={true}
            showDelete={false}
            disableEdit={!canEdit}
            size="small"
          />
        );
      }
    };
    return [...base, actionCol];
  }, [navigate, user]);

  return (
    <>
      <Divider style={{ marginTop: 0 }} />
      <div style={{ padding: '0 24px' }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={3} style={{ margin: 0 }}>RFI Forms</Title>
          </Col>
          <Col>
            <Button action="add" type="primary" icon={<PlusOutlined />} onClick={() => navigate('/qaqc/rfi/create')}>
              New RFI
            </Button>
          </Col>
        </Row>

        {/* Filters */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Input
              placeholder="Search subject"
              prefix={<SearchOutlined />}
              value={subjectText}
              onChange={(e) => setSubjectText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Input
              placeholder="Search recipients"
              prefix={<SearchOutlined />}
              value={recipientsSearch}
              onChange={(e) => setRecipientsSearch(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={3}>
            <Select
              allowClear
              placeholder="Status"
              mode="multiple"
              value={statusFilter}
              onChange={(v) => setStatusFilter(v)}
              options={[
                { label: 'Draft', value: 'draft' },
                { label: 'Active', value: 'active' },
                { label: 'Archived', value: 'archived' },
                { label: 'Cancelled', value: 'cancelled' },
              ]}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={24} md={8} lg={6}>
            <RangePicker
              style={{ width: '100%' }}
              value={dateRange as any}
              onChange={(vals) => setDateRange(vals as any)}
              placeholder={["Start date", "End date"]}
            />
          </Col>
        </Row>

        <Spin spinning={isLoading}>
          <StandardTable
            columns={columns as any}
            dataSource={filteredData}
            rowKey="id"
            loading={isLoading}
            pagination={getPaginationProps(filteredData.length)}
            scroll={{ x: 'max-content' }}
            bordered
            size="small"
          />
        </Spin>
      </div>
    </>
  );
};

export default QaqcRfiFormList;
